// Entry point for the build script in your package.json
import "@hotwired/turbo-rails"
import "./controllers"
import * as bootstrap from "bootstrap"
import Chart from "chart.js/auto"
import AOS from "aos"
import { syncThemeToggles } from "./theme"
import { isTurboNative, nativeSetBadge } from "./utils/native_bridge"
import "./sidebar_resize"
import "./persona_layout"
import "./keep_frame_content"

// Expose Chart globally so inline dashboard script can run on Turbo visits
window.Chart = Chart

// Initialize AOS (Animate On Scroll)
AOS.init({
    duration: 800,
    easing: "ease-out-cubic",
    once: true,
    offset: 50,
    disable: "mobile"
});

// Initialize Bootstrap tooltips on load and Turbo navigation (desktop hover
// only — coarse pointers keep title/aria-label fallbacks without sticky taps).
const initTooltips = () => {
    const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const tooltipTriggerList = Array.prototype.slice.call(
        document.querySelectorAll('[data-bs-toggle="tooltip"], [data-icon-tooltip]')
    );
    tooltipTriggerList.forEach((triggerEl) => {
        const existing = bootstrap.Tooltip.getInstance(triggerEl);
        if (existing) existing.dispose();
        if (finePointer) new bootstrap.Tooltip(triggerEl);
    });
};

document.addEventListener("turbo:load", initTooltips);
document.addEventListener("DOMContentLoaded", initTooltips);

// A header button's hover label (data-header-tip, CSS-only) has done its job
// once the button is clicked: hide it until the pointer leaves, so it doesn't
// sit beside a panel the click just opened.
document.addEventListener("click", (event) => {
    const button = event.target.closest?.("[data-header-tip]");
    if (!button) return;
    button.classList.add("is-tip-dismissed");
    button.addEventListener("mouseleave", () => button.classList.remove("is-tip-dismissed"), { once: true });
});

// Filter chips navigate list frames with data-turbo-action="advance"
// (promoted frame visits). On back/forward the frame's content can go stale
// relative to the restored URL: same-page frame promotions get no Turbo
// events at all, and cross-page restores render the cached snapshot with the
// last frame state. Re-sync tracked frames by fetching the restored URL (the
// controllers render just the frame for Turbo-Frame requests).
let resyncAfterPop = false;
let popResyncTimer = null;
const resyncTrackedListFrames = () => {
    resyncAfterPop = false;
    clearTimeout(popResyncTimer);
    document.querySelectorAll("turbo-frame[data-url-tracked]").forEach((frame) => {
        // The frame delegate keeps the promoted visit's action; left in
        // place, this resync would push a history entry of its own (pruning
        // the forward stack). This load must be history-neutral.
        if (frame.delegate) frame.delegate.action = null;
        frame.src = window.location.href;
    });
};
window.addEventListener("popstate", () => {
    resyncAfterPop = true;
    clearTimeout(popResyncTimer);
    // Same-page pops of promoted frame entries trigger no Turbo visit at
    // all; if none starts shortly, resync in place.
    popResyncTimer = setTimeout(() => {
        if (resyncAfterPop) resyncTrackedListFrames();
    }, 150);
});
document.addEventListener("turbo:visit", () => clearTimeout(popResyncTimer));
document.addEventListener("turbo:load", () => {
    if (resyncAfterPop) resyncTrackedListFrames();
});
document.addEventListener("turbo:load", syncThemeToggles);
document.addEventListener("DOMContentLoaded", syncThemeToggles);
if (document.readyState !== "loading") syncThemeToggles();

// Mirror due tasks + network badges onto the installed PWA icon (Badging API)
// or the iOS home-screen icon via the native bridge.
const syncAppBadge = () => {
    const meta = document.querySelector("meta[name='native-app-badge-count']") || document.querySelector("meta[name='tasks-due-count']")
    if (!meta) return
    const count = parseInt(meta.content || "0", 10)
    if (isTurboNative()) {
        nativeSetBadge(count)
        return
    }
    if (!("setAppBadge" in navigator)) return
    if (count > 0) navigator.setAppBadge(count).catch(() => {})
    else if ("clearAppBadge" in navigator) navigator.clearAppBadge().catch(() => {})
};

document.addEventListener("turbo:load", syncAppBadge);
document.addEventListener("DOMContentLoaded", syncAppBadge);
if (document.readyState !== "loading") syncAppBadge();

// Auto-dismiss server-rendered system toasts after a short delay.
const initFlash = () => {
    document.querySelectorAll("[data-system-toast]").forEach((node) => {
        setTimeout(() => node.remove(), 3500);
    });
};

document.addEventListener("turbo:load", initFlash);
document.addEventListener("DOMContentLoaded", initFlash);

// Confetti after completing all drafts (flag set in draft review modal)
const ensureConfetti = () => {
    if (typeof window.confetti === "function") return Promise.resolve();
    return new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = "https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.2/dist/confetti.browser.min.js";
        script.async = true;
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
    });
};

const maybeCelebrateDrafts = () => {
    try {
        const flag = localStorage.getItem("draftsAllDoneConfetti");
        if (!flag) return;
        localStorage.removeItem("draftsAllDoneConfetti");
        // give the page a moment to settle after Turbo load
        setTimeout(() => {
            ensureConfetti()
                .then(() => {
                    if (typeof window.confetti === "function") {
                        const burst = (originY) =>
                            window.confetti({
                                particleCount: 300,
                                spread: 120,
                                startVelocity: 45,
                                ticks: 220,
                                origin: { y: originY },
                                scalar: 1.1
                            });
                        burst(0.6);
                        setTimeout(() => burst(0.35), 200);
                    }
                })
                .catch(() => {});
        }, 350);
    } catch (e) {
        // ignore
    }
};

document.addEventListener("turbo:load", maybeCelebrateDrafts);
document.addEventListener("DOMContentLoaded", maybeCelebrateDrafts);

// Confetti after marking all emails as done
const maybeCelebrateMarkAllDone = () => {
    try {
        const flag = localStorage.getItem("markAllDoneConfetti");
        if (!flag) return;
        localStorage.removeItem("markAllDoneConfetti");
        setTimeout(() => {
            ensureConfetti()
                .then(() => {
                    if (typeof window.confetti === "function") {
                        // Big celebration burst
                        window.confetti({
                            particleCount: 400,
                            spread: 160,
                            startVelocity: 50,
                            ticks: 250,
                            origin: { y: 0.5 },
                            scalar: 1.2
                        });
                        setTimeout(() => {
                            window.confetti({
                                particleCount: 250,
                                angle: 60,
                                spread: 80,
                                origin: { x: 0, y: 0.6 },
                                startVelocity: 45
                            });
                            window.confetti({
                                particleCount: 250,
                                angle: 120,
                                spread: 80,
                                origin: { x: 1, y: 0.6 },
                                startVelocity: 45
                            });
                        }, 200);
                    }
                })
                .catch(() => {});
        }, 350);
    } catch (e) {
        // ignore
    }
};

document.addEventListener("turbo:load", maybeCelebrateMarkAllDone);
document.addEventListener("DOMContentLoaded", maybeCelebrateMarkAllDone);

// Local time formatting for elements with data-local-time
const initLocalTimes = () => {
    const nodes = document.querySelectorAll("[data-local-time]");
    if (!nodes.length) return;
    nodes.forEach((el) => {
        const val = el.getAttribute("data-local-time");
        if (!val) return;
        const dt = new Date(val);
        if (Number.isNaN(dt.getTime())) return;
        const fmt = el.getAttribute("data-local-format") || "medium";
        const options =
            fmt === "short"
                ? { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }
                : fmt === "date"
                    ? { month: "short", day: "numeric", year: "numeric" }
                    : { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" };
        try {
            el.textContent = new Intl.DateTimeFormat(undefined, options).format(dt);
        } catch (e) {
            // leave as-is on error
        }
    });
};

document.addEventListener("turbo:load", initLocalTimes);
document.addEventListener("DOMContentLoaded", initLocalTimes);
